import React, { useState, useCallback } from 'react';
import styled, { css } from 'styled-components';
import palette from '../../lib/styles/palette';
import SummaryModal from './SummaryModal';
import { inputNumberAutoComma } from '../../lib/commonFunc/commonFunc';
import { GoReport } from 'react-icons/go';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import categoryMap from '../../lib/def/category';

const MonthlyTableBlock = styled.div`
  flex: 1;
  padding: 12px 16px;
`;

const CategoryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 14px;
  background: #f7f8fa;
  border-radius: 8px;
  border-left: 3px solid ${palette.webCyan[5]};
  cursor: pointer;
  transition: background 0.15s;
  user-select: none;

  &:hover {
    background: #eff0f3;
  }
`;

const CategoryLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const CategoryTitle = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #333;
  font-family: 'Noto Sans KR', sans-serif;
  white-space: nowrap;
`;

const CategorySub = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #aaa;
  font-family: 'Noto Sans KR', sans-serif;
  white-space: nowrap;
`;

const CategoryRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CategoryCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #888;
  font-family: 'Noto Sans KR', sans-serif;
  white-space: nowrap;
`;

const CategoryPrice = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #222;
  font-family: 'Noto Sans KR', sans-serif;
  white-space: nowrap;
  min-width: 80px;
  text-align: right;
`;

const ArrowIcon = styled.div`
  display: flex;
  align-items: center;
  color: #aaa;
  font-size: 20px;
  transition: transform 0.2s;
`;

const InfoIcon = styled.div`
  display: flex;
  align-items: center;
  padding: 2px;
  border-radius: 4px;
  transition: background 0.15s;

  &:hover {
    background: #ddd;
  }

  svg {
    font-size: 15px;
    color: #999;
  }
`;

const ItemList = styled.div`
  padding-left: 16px;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 14px;

  &:first-child {
    padding-top: 8px;
  }
  &:last-child {
    padding-bottom: 8px;
  }
`;

const ItemName = styled.div`
  flex: 3;
  font-size: 13px;
  color: #555;
  font-family: 'Noto Sans KR', sans-serif;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const ItemCount = styled.div`
  flex: 1;
  font-size: 12px;
  color: #999;
  text-align: right;
  font-family: 'Noto Sans KR', sans-serif;
  white-space: nowrap;
`;

const ItemPrice = styled.div`
  flex: 2;
  font-size: 13px;
  font-weight: 600;
  color: #444;
  text-align: right;
  font-family: 'Noto Sans KR', sans-serif;
  white-space: nowrap;
`;

const CategoryBlock = styled.div`
  margin-bottom: 6px;
`;

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0 4px 10px;
`;

const OptionLabel = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 13px;
  color: #888;
  font-family: 'Noto Sans KR', sans-serif;
  user-select: none;

  input {
    cursor: pointer;
    width: 15px;
    height: 15px;
    margin-right: 5px;
  }
`;

const MonthlyTable = ({ items, monthOption, memberships, goods, selectedDay, onToggleDisableNoneItem }) => {
  const [visible, setVisible] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [openCategories, setOpenCategories] = useState({});

  const onClick = useCallback(() => {
    setVisible(true);
  }, []);

  const onClose = useCallback(() => {
    setVisible(false);
  }, []);

  const toggleCategory = (category) => {
    setOpenCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  const income = (category, item) => {
    if (!memberships[category]) return "0";
    return inputNumberAutoComma(
      memberships[category].reduce((total, m) => {
        if (item.id === m.clubItemID) return (total += m.price);
        return total;
      }, 0),
    );
  };

  const registrationStatus = (category) => {
    let initState = { new: 0, old: 0, newbeeSum: 0, oldbeeSum: 0 };
    if (!memberships[category]) return initState;

    const result = memberships[category].reduce((count, m) => {
      if (m.isNew === 1)
        return {
          ...count,
          new: (count.new += 1),
          newbeeSum: (count.newbeeSum += m.price),
        };
      else
        return {
          ...count,
          old: (count.old += 1),
          oldbeeSum: (count.oldbeeSum += m.price),
        };
    }, initState);
    return result;
  };

  const count = (category, item) => {
    if (!memberships[category]) return 0;

    return memberships[category].reduce((count, m) => {
      if (item.id === m.clubItemID) return (count += 1);
      return count;
    }, 0);
  };

  const goodsCount = (category, item) => {
    if (!goods[category]) return 0;

    let clickedDay = new Date(selectedDay);

    return goods[category].filter(item => new Date(item.registTime * 1000).getFullYear() == clickedDay.getFullYear() &&
    new Date(item.registTime * 1000).getMonth() == clickedDay.getMonth()).reduce((purchaseCount, m) => {
      if (item.id === m.clubItemID) return (purchaseCount += m.count);
      return purchaseCount;
    }, 0);
  };

  const totalCount = (category) => {
    if (!memberships[category]) return 0;
    return memberships[category].length;
  };

  const totalIncome = (category) => {
    if (!memberships[category]) return 0;
    return memberships[category].reduce((sum, m) => {
      return (sum += m.price);
    }, 0);
  };

  const lockerTotalCount = () => {
    if (memberships != null && memberships['락커']) {
      return memberships['락커'].length;
    }
    return 0;
  };

  const lockerTotalPrice = () => {
    let result = 0;
    if (memberships != null && memberships['락커']) {
      for (let i = 0; i < memberships['락커'].length; i++) {
        result += memberships['락커'][i].salePrice;
      }
      return result;
    }
    return 0;
  };

  if (!memberships) return false;

  return (
    <MonthlyTableBlock>
      <OptionRow>
        <OptionLabel onClick={onToggleDisableNoneItem}>
          <input type="checkbox" checked={monthOption.disableNoneItem} readOnly />
          판매 기록 없는 상품 숨기기
        </OptionLabel>
      </OptionRow>
      {items &&
        Object.keys(items)
          .sort((a, b) => (categoryMap[a] > categoryMap[b] ? 1 : -1))
          .map((category, idx1) => {
            const regStatus = registrationStatus(category);
            const isOpen = openCategories[category] || false;
            return (
              <CategoryBlock key={idx1}>
                <CategoryRow onClick={() => toggleCategory(category)}>
                  <CategoryLeft>
                    <ArrowIcon>
                      {isOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
                    </ArrowIcon>
                    <CategoryTitle>{category}</CategoryTitle>
                    {category != "일반" &&
                      <CategorySub>
                        신규 {regStatus.new}명 / 재등록 {regStatus.old}명
                      </CategorySub>
                    }
                    {category != "일반" &&
                      <InfoIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          onClick();
                          setModalData({
                            ...regStatus,
                            category,
                          });
                        }}
                      >
                        <GoReport />
                      </InfoIcon>
                    }
                    <SummaryModal
                      visible={visible}
                      onClose={onClose}
                      category={category}
                      data={modalData}
                    />
                  </CategoryLeft>
                  <CategoryRight>
                    <CategoryCount>{inputNumberAutoComma(totalCount(category))}명</CategoryCount>
                    <CategoryPrice>{inputNumberAutoComma(totalIncome(category))}원</CategoryPrice>
                  </CategoryRight>
                </CategoryRow>
                {isOpen &&
                  <ItemList>
                    {items[category].map((item, idx2) => {
                      if (item.isDelete === 1 && !memberships[category])
                        return null;
                      if (
                        item.isDelete === 1 &&
                        memberships[category].find(
                          (m) => m.clubItemID === item.id,
                        ) === undefined
                      ) {
                        return null;
                      }

                      if (monthOption.disableNoneItem && count(category, item) === 0) return null;

                      return (
                        <ItemRow key={item.id}>
                          <ItemName>{item.title}</ItemName>
                          {category == "일반"
                            ? <ItemCount>{goodsCount(category, item)}개</ItemCount>
                            : <ItemCount>{count(category, item)}명</ItemCount>
                          }
                          <ItemPrice>{income(category, item)}원</ItemPrice>
                        </ItemRow>
                      );
                    })}
                  </ItemList>
                }
              </CategoryBlock>
            );
          })}
      <CategoryBlock>
        <CategoryRow onClick={() => toggleCategory('락커')}>
          <CategoryLeft>
            <ArrowIcon>
              {openCategories['락커'] ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
            </ArrowIcon>
            <CategoryTitle>락커</CategoryTitle>
          </CategoryLeft>
          <CategoryRight>
            <CategoryCount>{lockerTotalCount()}명</CategoryCount>
            <CategoryPrice>{inputNumberAutoComma(lockerTotalPrice())}원</CategoryPrice>
          </CategoryRight>
        </CategoryRow>
        {openCategories['락커'] &&
          <ItemList>
            <ItemRow>
              <ItemName>락커</ItemName>
              <ItemCount>{lockerTotalCount()}명</ItemCount>
              <ItemPrice>{inputNumberAutoComma(lockerTotalPrice())}원</ItemPrice>
            </ItemRow>
          </ItemList>
        }
      </CategoryBlock>
    </MonthlyTableBlock>
  );
};

export default MonthlyTable;